import { Component, Input, OnInit, OnDestroy } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { slice } from 'lodash';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { TVEyesArchivedStateEnum } from '../../../../api/mentions/enum/tv-eyes-archived-state.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { PrecisionClipLoadingDialogComponent } from '../../../Shared/components/PrecisionClipLoadingDialog/precision-clip-loading-dialog.component';
import { MonitoringPrecisionClipService, PrecisionResourceType, PrecisionConfig } from '../../../Shared/services/monitoring-precision-clip.service';
import { KeywordSelectionEnum } from '@Monitor/Shared/enums/precision-keyword-selection.enum';
import { MentionSearchModel } from '@Monitor/Shared/models/mention-search.model';
import { BriefingsService } from '../../services/briefings.service';
import { CuratedMentionListDisplayElementModel } from '../../models/curated-mention-list-display-element.model';


enum MentionSelectionEnum {
    MENTIONS_IN_SECTION = 'MENTIONS_IN_SECTION',
    ALL_SAVED_MENTIONS = 'ALL_SAVED_MENTIONS'
}


@Component({
    selector: 'agpr-precision-clip-setup-takeover',
    templateUrl: './precision-clip-setup-takeover.component.html',
    styleUrls: ['./precision-clip-setup-takeover.component.scss'],
    standalone: false
})
export class PrecisionClipSetupTakeoverComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() resourceId: number;

    @Input() overlayContainer: Element;
    @Input() protected animationTrigger: BehaviorSubject<FullProfileAnimationModel>;

    hideSlideOverlay: boolean = false;

    keywordSelection: KeywordSelectionEnum = KeywordSelectionEnum.SAVED_MENTION;
    mentionSelection: MentionSelectionEnum = MentionSelectionEnum.MENTIONS_IN_SECTION;

    manualKeywords: string[] = [];
    fallbackKeywords: string[] = [];

    insightsEnabled: boolean = true;

    extendStartTime: number = 5;
    extendedEndTime: number = 5;

    numPrecisionClipMentions: number = 0;
    precisionClipTooltip: string = '';

    curatedBlockName: string;

    private allPrecisionClipMentions: Mention[] = [];
    private blockPrecisionClipMentions: Mention[] = [];

    private mentionSearch: MentionSearchModel;
    private subscriptions: Subscription[] = [];

    readonly id = 'precisionClipSetupTakeover';
    readonly buttonType = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly keywordSelectionEnum = KeywordSelectionEnum;
    readonly mentionSelectionEnum = MentionSelectionEnum;
    readonly MENTION_LIMIT: number = 100;

    constructor(
        public coreDialogService: CoreDialogService,
        private precisionClipService: MonitoringPrecisionClipService,
        private briefingsService: BriefingsService,
    ) {
        super(coreDialogService, 'PrecisionClipSetupTakeover');
    }

    ngOnInit() {
        this.subscriptions.push(
            this.briefingsService.getCurrentBriefingMentions().subscribe(
                mentions => {
                    this.allPrecisionClipMentions = mentions?.filter(mention => mention.isPrecisionClipable()) ?? [];
                    this.handleNumberOfMentions();
                }
            ),
            this.briefingsService.getSelectedBlock().pipe(filter(x => !!x)).subscribe(
                block => {
                    const displayElement = block.displayElement as CuratedMentionListDisplayElementModel;

                    const mentions = displayElement.mentions.map(mention => new Mention(mention));
                    this.blockPrecisionClipMentions = mentions?.filter(mention => mention.isPrecisionClipable()) ?? [];

                    this.mentionSearch = displayElement.searchCriteria;
                    this.curatedBlockName = displayElement.title;
                    this.handleNumberOfMentions();
                }
            ),
        );
    }

    confirm() {
        this.close();

        const payload = this.createPayload();

        if (payload?.mentions?.length === 0) {
            return;
        }

        this.precisionClipService.start(this.resourceId, PrecisionResourceType.BRIEFING, payload);

        const io = {
            inputs: {
                resourceId: this.resourceId,
                resourceType: PrecisionResourceType.BRIEFING
            },
            outputs: {
                dialogClose: () => {}
            }
        };
        this.coreDialogService.open(PrecisionClipLoadingDialogComponent, io);
    }

    createPayload(): PrecisionConfig {
        const mentions = this.mentionSelection === MentionSelectionEnum.ALL_SAVED_MENTIONS
            ? this.allPrecisionClipMentions : this.blockPrecisionClipMentions;

        const payload: PrecisionConfig = {
            keywordSelection: this.keywordSelection,
            manualKeywords: this.manualKeywords,
            fallbackKeywords: this.fallbackKeywords,
            mentions: slice(mentions, 0, this.MENTION_LIMIT),
            mentionSearch: this.mentionSearch,
            insightsEnabled: this.insightsEnabled,
            extendStartTime: this.extendStartTime,
            extendedEndTime: this.extendedEndTime
        };
        return payload;
    }


    cancel() {
        this.closeDialog();
    }

    handleNumberOfMentions() {
        this.numPrecisionClipMentions = this.precisionClipService.getClipableMentions(this.createPayload())?.length || 0;
        this.precisionClipTooltip = (this.numPrecisionClipMentions === 0) ? 'Briefings.blockType.precisionClip.noClipsToGenerate' : '';
    }

    onVisibleChange(visible: boolean) {
        if (!visible) {
            this.closeDialog();
        }
    }

    keywordSelectionChange(value: KeywordSelectionEnum) {
        if (value === KeywordSelectionEnum.MANUAL && this.manualKeywords?.length === 0) {
            this.precisionClipTooltip = 'Briefings.blockType.precisionClip.noManualKeywords';
        } else {
            this.precisionClipTooltip = '';
        }
        this.handleNumberOfMentions();
    }

    close() {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
